/**
 * Real, OAuth-backed payslip PDF + salary-disbursement-file + payment-source
 * -account resolvers for the run-payroll wizard's post-run step (C8,
 * #22330) — the REAL counterpart to the payslip/disbursement-file/bank
 * -account-picker THIRD of the now-DELETED `dev_pay_run_post_actions.ts`,
 * reachable in every environment (including production), not just local dev
 * — same "real production OAuth-gated work" stance as
 * `oauth_entity_context.ts` / `use_my_pay.ts` / `use_session_facts.ts`.
 *
 * Statutory-payment recording/voiding and the filing calendar (the OTHER
 * two thirds of the deleted dev module) are OUT OF SCOPE here — C9 (#22331)
 * migrated them to their own sibling module, `oauth_statutory_filing.ts`,
 * the same way this module migrates payslip/disbursement/bank-accounts.
 *
 * Uses the real bearer client (`getTrpcClient()`, `lib/trpc.ts`) — never
 * `credentialedFetch`/`dev_auth_bypass.ts`'s cookie-session transport, and
 * never `resolveDevEntityContext`'s `firm.list`/`entity.list` round trip.
 * `firm_id`/`entity_id` are supplied BY THE CALLER (resolved once via
 * `useOAuthEntityContext()` in `use_pay_run_post_actions.ts`), exactly like
 * `use_session_facts.ts`'s `entityUser.me`/`entity.clientGet` calls.
 *
 * ── The entitlement gate ──────────────────────────────────────────────────
 *
 * `generatePayslip`, `getSalaryDisbursementFile`, and
 * `listPaymentSourceAccounts` are all `payrollEntityProcedure` (query-only),
 * gated by the SAME single `payroll` add-on entitlement
 * (`requirePayrollAddon`, verified directly against
 * `apps/api/src/trpc/routers/payroll.ts`) and by NOTHING else product-tier
 * -related. A FORBIDDEN from any of the three is classified via
 * `isPayrollAddonRequiredError` (`lib/trpc.ts`) — see that function's
 * docblock for why a bare `err.data?.code === 'FORBIDDEN'` check is NOT
 * sufficient (the same procedure family can ALSO throw FORBIDDEN for a
 * missing OAuth scope, a null-audience token, or no entity access at all;
 * only the add-on-specific message classifies as `entitlement_required`,
 * everything else degrades to a generic `error`).
 *
 * ── Why `getSalaryDisbursementFile`'s `content` is a plain string ─────────
 *
 * Same reasoning as `oauth_statutory_filing.ts`'s module docblock:
 * `services/payroll/salary_disbursement.ts`'s exporters always produce a
 * `text/csv`/`text/plain` body, never binary, so `content` downloads as-is
 * via a `Blob` — unlike `generatePayslip`'s `pdf_base64` (binary PDF bytes).
 *
 * ── Never throws ──────────────────────────────────────────────────────────
 *
 * Every function here resolves a discriminated `{ ok: false; reason: ... }`
 * result on failure and logs one actionable message — never a mock
 * fallback, never a fabricated success (mirrors `use_my_pay.ts`'s "no mock
 * fallback, real data or an explicit error state" stance exactly).
 */
import { getTrpcClient, isPayrollAddonRequiredError } from '../trpc.js';

// ── Shared wire types ────────────────────────────────────────────────────

/** The four bank disbursement exporters registered server-side — matches `DISBURSEMENT_BANK_ID` in `apps/api/src/trpc/routers/payroll.ts`. `post_run_actions_calculations.ts`'s `validate_disbursement_input` imports this exact type (it used to import an identically-shaped `RealDisbursementBankId` from the now-deleted `dev_pay_run_post_actions.ts`). */
export type OAuthDisbursementBankId = 'sampath' | 'combank' | 'hnb' | 'boc';

/**
 * `payroll.listPaymentSourceAccounts`'s (#22324, C5) response row shape,
 * narrowed to what the disbursement dialog's bank-account picker needs — an
 * intentional subset, not a mirror. This SAME procedure is also now (C9,
 * #22331) reused for the statutory-payment picker (`use_pay_run_post_
 * actions.ts`'s `list_bank_accounts`, mapped down to the narrower
 * `BankAccountOption` shape) — the two pickers share this one network call
 * rather than each maintaining its own, since `account.list` (the statutory
 * picker's original, dev-bypass-only source) is deliberately not
 * OAuth-allowlisted for a payroll token.
 */
export interface RealPaymentSourceAccountRow {
  id: string;
  name: string;
  code: string | null;
  currency_code: string | null;
}

/** How an OAuth-backed payroll call can fail, once classified. */
export type OAuthPayrollFailureReason = 'entitlement_required' | 'error';

// ── Deps ─────────────────────────────────────────────────────────────────

export interface OAuthPayRunPostActionsDeps {
  generatePayslip: (input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
    employee_id: string;
    locale: 'en' | 'si' | 'ta';
  }) => Promise<{ pdf_base64: string; filename: string }>;
  getSalaryDisbursementFile: (input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
    bank_id: OAuthDisbursementBankId;
    debit_account_no: string;
    value_date: string;
  }) => Promise<{
    filename: string;
    mime_type: string;
    content: string;
    total_cents: number;
    line_count: number;
    bank_name: string;
    warnings: string[];
  }>;
  listPaymentSourceAccounts: (input: {
    firm_id: string;
    entity_id: string;
  }) => Promise<{ accounts: RealPaymentSourceAccountRow[] }>;
}

function defaultDeps(): OAuthPayRunPostActionsDeps {
  const client = getTrpcClient();
  return {
    generatePayslip: (input) => client.payroll.generatePayslip.query(input),
    getSalaryDisbursementFile: (input) =>
      client.payroll.getSalaryDisbursementFile.query(input),
    listPaymentSourceAccounts: (input) =>
      client.payroll.listPaymentSourceAccounts.query(input),
  };
}

// ── Payslip download ─────────────────────────────────────────────────────

export type GeneratePayslipResult =
  | { ok: true; pdf_base64: string; filename: string }
  | { ok: false; reason: 'entitlement_required' }
  | { ok: false; reason: 'error'; message: string };

/** Downloads one employee's payslip PDF for a given pay run. Never throws. */
export async function fetchGeneratedPayslip(
  input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
    employee_id: string;
    locale: 'en' | 'si' | 'ta';
  },
  deps: OAuthPayRunPostActionsDeps = defaultDeps(),
): Promise<GeneratePayslipResult> {
  try {
    const { pdf_base64, filename } = await deps.generatePayslip(input);
    return { ok: true, pdf_base64, filename };
  } catch (err) {
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] generate payslip: entity does not have an active payroll add-on entitlement.',
      );
      return { ok: false, reason: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] generate payslip: generatePayslip failed',
      err,
    );
    return {
      ok: false,
      reason: 'error',
      message:
        err instanceof Error ? err.message : 'unknown error generating payslip',
    };
  }
}

// ── Salary disbursement file ─────────────────────────────────────────────

export type GetSalaryDisbursementFileResult =
  | {
      ok: true;
      filename: string;
      mime_type: string;
      content: string;
      total_cents: number;
      line_count: number;
      bank_name: string;
      warnings: string[];
    }
  | { ok: false; reason: 'entitlement_required' }
  | { ok: false; reason: 'error'; message: string };

/**
 * Generates the salary disbursement file for an approved pay run. Never
 * throws; the caller is responsible for only enabling this action once the
 * pay run is `approved` (the server itself also enforces this and returns a
 * clear `BAD_REQUEST` reason otherwise, surfaced verbatim via `message`).
 */
export async function fetchSalaryDisbursementFile(
  input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
    bank_id: OAuthDisbursementBankId;
    debit_account_no: string;
    value_date: string;
  },
  deps: OAuthPayRunPostActionsDeps = defaultDeps(),
): Promise<GetSalaryDisbursementFileResult> {
  try {
    const result = await deps.getSalaryDisbursementFile(input);
    return { ok: true, ...result };
  } catch (err) {
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] get salary disbursement file: entity does not have an active payroll add-on entitlement.',
      );
      return { ok: false, reason: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] get salary disbursement file: getSalaryDisbursementFile failed',
      err,
    );
    return {
      ok: false,
      reason: 'error',
      message:
        err instanceof Error
          ? err.message
          : 'unknown error generating disbursement file',
    };
  }
}

// ── Payment-source bank-account picker (disbursement flow) ──────────────

export type ListPaymentSourceAccountsResult =
  | { ok: true; accounts: RealPaymentSourceAccountRow[] }
  | { ok: false; reason: 'entitlement_required' }
  | { ok: false; reason: 'error' };

/**
 * Lists the entity's bank/cash accounts eligible as a salary-disbursement
 * debit source, via C5's narrow `payroll.listPaymentSourceAccounts` (#22324)
 * — never the general `account.list`, which is deliberately NOT allowlisted
 * for an OAuth payroll token (C5's own design decision: a payroll-scoped
 * token should never be able to read the entity's whole chart of accounts).
 * Never throws.
 */
export async function fetchPaymentSourceAccounts(
  input: { firm_id: string; entity_id: string },
  deps: OAuthPayRunPostActionsDeps = defaultDeps(),
): Promise<ListPaymentSourceAccountsResult> {
  try {
    const { accounts } = await deps.listPaymentSourceAccounts(input);
    return { ok: true, accounts };
  } catch (err) {
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] list payment source accounts: entity does not have an active payroll add-on entitlement.',
      );
      return { ok: false, reason: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] list payment source accounts: listPaymentSourceAccounts failed',
      err,
    );
    return { ok: false, reason: 'error' };
  }
}
